import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Save, FileText } from 'lucide-react';
import supabase from '../../supabase';
import { useToast } from '../Invoice/Toast'; // Make sure path is correct
import './EditInvoicePopup.css';

const EditInvoicePopup = ({ invoice, isOpen, onClose, onSave, clients }) => {
  const [formData, setFormData] = useState({
    invoice_number: '',
    issue_date: '',
    due_date: '',
    notes: '',
    client_id: '',
    termsandconditions: [],
    discount: 0,
    taxType: 'NONE',
    gstRate: 18,
    igstRate: 18
  });
  const [items, setItems] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [clientsList, setClientsList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize toast hook
  const { showError, showSuccess, showWarning, ToastContainer } = useToast();

  useEffect(() => {
    if (invoice && isOpen) {
      fetchInvoiceData();
    }
  }, [invoice, isOpen]);

  const fetchInvoiceData = async () => {
    if (!invoice) return;

    setLoading(true);
    try {
      const [
        { data: invoiceItems },
        { data: products },
        { data: clientsData }
      ] = await Promise.all([
        supabase.from('invoice_items').select('*').eq('invoice_id', invoice.id),
        supabase.from('products').select('*').eq('user_id', invoice.user_id),
        supabase.from('clients').select('*').eq('user_id', invoice.user_id)
      ]);

      // Determine tax type from first item
      let taxType = 'NONE';
      let gstRate = 18;
      let igstRate = 18;
      
      if (invoiceItems && invoiceItems.length > 0) {
        const firstItem = invoiceItems[0];
        if (firstItem.sgst_percent > 0 && firstItem.cgst_percent > 0) {
          taxType = 'SGST_CGST';
          gstRate = (firstItem.sgst_percent + firstItem.cgst_percent);
        } else if (firstItem.sgst_percent > 0 && firstItem.cgst_percent === 0) {
          taxType = 'IGST';
          igstRate = firstItem.sgst_percent;
        }
      }

      setFormData({
        invoice_number: invoice.invoice_number || '',
        issue_date: invoice.issue_date || '',
        due_date: invoice.due_date || '',
        notes: invoice.notes || '',
        client_id: invoice.client_id || '',
        termsandconditions: invoice.termsandconditions ? invoice.termsandconditions.split('|') : [''],
        discount: Number(invoice.discount) || 0,
        taxType,
        gstRate,
        igstRate
      });

      setItems(invoiceItems?.map(item => ({
        id: item.id,
        product_id: '',
        product_name: item.name || '',
        description: item.description || '',
        hsn_code: item.hsn_sac || '',
        quantity: item.quantity || 1,
        unit_price: item.rate_per_item || 0,
        isExisting: true
      })) || []);

      setAvailableItems(products || []);
      setClientsList(clientsData || []);
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      showError('Loading Error', 'Failed to load invoice data. Please try again.');
    }
    setLoading(false);
  };

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  }, []);

  const handleItemChange = useCallback((index, field, value) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, []);

  const handleProductSelect = useCallback((index, productId) => {
    const product = availableItems.find(p => p.product_id === parseInt(productId));
    if (product) {
      handleItemChange(index, 'product_id', productId);
      handleItemChange(index, 'product_name', product.product_name);
      handleItemChange(index, 'unit_price', product.price_selling || 0);
      handleItemChange(index, 'hsn_code', product.hsn_code || '');
      handleItemChange(index, 'description', product.description || '');
    }
  }, [availableItems, handleItemChange]);

  const addItem = useCallback(() => {
    setItems(prev => [...prev, {
      id: null,
      product_id: '',
      product_name: '',
      description: '',
      hsn_code: '',
      quantity: 1,
      unit_price: 0,
      isExisting: false
    }]);
  }, []);

  const removeItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const addTerm = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      termsandconditions: [...prev.termsandconditions, '']
    }));
  }, []);

  const removeTerm = useCallback((index) => {
    setFormData(prev => ({
      ...prev,
      termsandconditions: prev.termsandconditions.filter((_, i) => i !== index)
    }));
  }, []);

  const handleTermChange = useCallback((index, value) => {
    setFormData(prev => ({
      ...prev,
      termsandconditions: prev.termsandconditions.map((term, i) => i === index ? value : term)
    }));
  }, []);

  const calculateItemTotal = (item) => {
    return (item.quantity || 0) * (item.unit_price || 0);
  };

  // Calculate subtotal
  const calculateSubtotal = () => {
    return items.reduce((sum, item) => sum + calculateItemTotal(item), 0);
  };

  // Calculate discount amount
  const calculateDiscountAmount = () => {
    return calculateSubtotal() * ((formData.discount || 0) / 100);
  };

  // Calculate after discount
  const calculateAfterDiscount = () => {
    return calculateSubtotal() - calculateDiscountAmount();
  };

  // Calculate tax amounts
  const calculateTaxAmounts = () => {
    const afterDiscount = calculateAfterDiscount();
    const { taxType, gstRate, igstRate } = formData;
    
    let sgstTotal = 0, cgstTotal = 0, igstTotal = 0, totalTax = 0;
    
    if (taxType === 'SGST_CGST') {
      const sgstRate = gstRate / 2;
      const cgstRate = gstRate / 2;
      sgstTotal = afterDiscount * (sgstRate / 100);
      cgstTotal = afterDiscount * (cgstRate / 100);
      totalTax = sgstTotal + cgstTotal;
    } else if (taxType === 'IGST') {
      igstTotal = afterDiscount * (igstRate / 100);
      totalTax = igstTotal;
    }
    
    return { sgstTotal, cgstTotal, igstTotal, totalTax };
  };

  // Calculate grand total
  const calculateGrandTotal = () => {
    return calculateAfterDiscount() + calculateTaxAmounts().totalTax;
  };

  const handleSave = async () => {
    if (!formData.invoice_number.trim()) {
      showError('Validation Error', 'Invoice number is required');
      return;
    }

    setSaving(true);
    try {
      const subtotal = calculateSubtotal();
      const { totalTax } = calculateTaxAmounts();
      const totalAmount = calculateGrandTotal();

      // Update invoice
      const { error: invoiceError } = await supabase
        .from('invoices')
        .update({
          invoice_number: formData.invoice_number,
          issue_date: formData.issue_date,
          due_date: formData.due_date,
          notes: formData.notes,
          client_id: formData.client_id,
          termsandconditions: formData.termsandconditions.filter(term => term.trim()).join('|'),
          subtotal: subtotal,
          total_tax: totalTax,
          total_amount: totalAmount
        })
        .eq('id', invoice.id);

      if (invoiceError) throw invoiceError;

      // Delete existing items and insert new ones
      await supabase.from('invoice_items').delete().eq('invoice_id', invoice.id);

      const validItems = items.filter(item => item.product_name && item.unit_price > 0);
      if (validItems.length > 0) {
        const itemsToInsert = validItems.map(item => {
          const qty = item.quantity || 0;
          const base = qty * (item.unit_price || 0);
          
          // Calculate tax per item based on tax type
          let sgstPercent = 0, cgstPercent = 0;
          
          if (formData.taxType === 'SGST_CGST') {
            sgstPercent = formData.gstRate / 2;
            cgstPercent = formData.gstRate / 2;
          } else if (formData.taxType === 'IGST') {
            sgstPercent = formData.igstRate;
            cgstPercent = 0;
          }
          
          return {
            invoice_id: invoice.id,
            name: item.product_name,
            description: item.description || '',
            hsn_sac: item.hsn_code || '',
            quantity: qty,
            rate_per_item: item.unit_price || 0,
            sgst_percent: sgstPercent,
            cgst_percent: cgstPercent
          };
        });

        const { error: itemsError } = await supabase
          .from('invoice_items')
          .insert(itemsToInsert);

        if (itemsError) throw itemsError;
      }

      showSuccess('Success', 'Invoice updated successfully!');
      
      // Close after a brief delay to let user see the success message
      setTimeout(() => {
        onSave();
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error saving invoice:', error);
      showError('Save Error', error.message || 'Failed to save invoice. Please try again.');
    }
    setSaving(false);
  };

  if (!isOpen) return null;

  const { sgstTotal, cgstTotal, igstTotal } = calculateTaxAmounts();

  return (
    <>
      {/* Toast Container - Rendered outside overlay for proper positioning */}
      <ToastContainer />
      
      <div className="edit-invoice-overlay">
        <div className="edit-invoice-popup">
          <div className="edit-invoice-header">
            <h2 className="edit-invoice-title">
              <FileText size={24} />
              Edit Invoice
            </h2>
            <button className="edit-invoice-close" onClick={onClose}>
              <X size={20} />
            </button>
          </div>

          <div className="edit-invoice-content">
            {loading ? (
              <div className="edit-invoice-loading">
                <div className="loading-spinner"></div>
                <p>Loading invoice data...</p>
              </div>
            ) : (
              <>
                {/* Invoice Details Section */}
                <div className="edit-section">
                  <h3 className="section-title">Invoice Details</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Invoice Number</label>
                      <input
                        type="text"
                        value={formData.invoice_number}
                        onChange={(e) => handleFormChange('invoice_number', e.target.value)}
                        placeholder="Enter invoice number"
                      />
                    </div>
                    <div className="form-group">
                      <label>Issue Date</label>
                      <input
                        type="date"
                        value={formData.issue_date}
                        onChange={(e) => handleFormChange('issue_date', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Due Date</label>
                      <input
                        type="date"
                        value={formData.due_date}
                        onChange={(e) => handleFormChange('due_date', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Tax Configuration */}
                <div className="edit-section">
                  <h3 className="section-title">Tax Configuration</h3>
                  <div className="form-grid">
                    <div className="form-group">
                      <label>Tax Type</label>
                      <select
                        value={formData.taxType}
                        onChange={(e) => handleFormChange('taxType', e.target.value)}
                      >
                        <option value="NONE">No Tax</option>
                        <option value="SGST_CGST">SGST + CGST</option>
                        <option value="IGST">IGST</option>
                      </select>
                    </div>
                    {formData.taxType === 'SGST_CGST' && (
                      <div className="form-group">
                        <label>GST Rate (%)</label>
                        <input
                          type="number"
                          value={formData.gstRate}
                          onChange={(e) => handleFormChange('gstRate', parseFloat(e.target.value) || 0)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                    )}
                    {formData.taxType === 'IGST' && (
                      <div className="form-group">
                        <label>IGST Rate (%)</label>
                        <input
                          type="number"
                          value={formData.igstRate}
                          onChange={(e) => handleFormChange('igstRate', parseFloat(e.target.value) || 0)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                      </div>
                    )}
                    <div className="form-group">
                      <label>Discount (%)</label>
                      <input
                        type="number"
                        value={formData.discount}
                        onChange={(e) => handleFormChange('discount', parseFloat(e.target.value) || 0)}
                        min="0"
                        max="100"
                        step="0.01"
                      />
                    </div>
                  </div>
                </div>

                {/* Items Section */}
                <div className="edit-section">
                  <div className="section-header">
                    <h3 className="section-title">Items</h3>
                    <button className="add-item-btn" onClick={addItem}>
                      <Plus size={16} />
                      Add Item
                    </button>
                  </div>

                  <div className="items-container">
                    {items.map((item, index) => (
                      <div key={index} className="item-card">
                        <div className="item-header">
                          <span className="item-number">Item {index + 1}</span>
                          {items.length > 1 && (
                            <button
                              className="remove-item-btn"
                              onClick={() => removeItem(index)}
                            >
                              <Trash2 size={16} />
                            </button>
                          )}
                        </div>

                        <div className="item-grid">
                          <div className="form-group">
                            <label>Product/Service</label>
                            <input
                              type="text"
                              value={item.product_name}
                              onChange={(e) => handleItemChange(index, 'product_name', e.target.value)}
                              placeholder="Enter product/service name"
                            />
                          </div>
                          <div className="form-group">
                            <label>Description</label>
                            <input
                              type="text"
                              value={item.description}
                              onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                              placeholder="Enter description"
                            />
                          </div>
                          <div className="form-group">
                            <label>HSN/SAC Code</label>
                            <input
                              type="text"
                              value={item.hsn_code}
                              onChange={(e) => handleItemChange(index, 'hsn_code', e.target.value)}
                              placeholder="Enter HSN/SAC code"
                            />
                          </div>
                          <div className="form-group">
                            <label>Quantity</label>
                            <input
                              type="number"
                              value={item.quantity}
                              onChange={(e) => handleItemChange(index, 'quantity', parseInt(e.target.value) || 0)}
                              min="0"
                            />
                          </div>
                          <div className="form-group">
                            <label>Unit Price</label>
                            <input
                              type="number"
                              value={item.unit_price}
                              onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
                              min="0"
                              step="0.01"
                            />
                          </div>
                          <div className="form-group">
                            <label>Total</label>
                            <input
                              type="text"
                              value={calculateItemTotal(item).toFixed(2)}
                              className="calculated-field"
                              readOnly
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Price Summary Section */}
                  <div className="price-breakdown">
                    <div className="price-row">
                      <span>Subtotal:</span>
                      <span>₹{calculateSubtotal().toFixed(2)}</span>
                    </div>

                    {formData.discount > 0 && (
                      <>
                        <div className="price-row">
                          <span>Discount ({formData.discount}%):</span>
                          <span>-₹{calculateDiscountAmount().toFixed(2)}</span>
                        </div>
                        <div className="price-row">
                          <span>After Discount:</span>
                          <span>₹{calculateAfterDiscount().toFixed(2)}</span>
                        </div>
                      </>
                    )}

                    {formData.taxType === 'SGST_CGST' && (
                      <>
                        <div className="price-row">
                          <span>SGST ({formData.gstRate / 2}%):</span>
                          <span>₹{sgstTotal.toFixed(2)}</span>
                        </div>
                        <div className="price-row">
                          <span>CGST ({formData.gstRate / 2}%):</span>
                          <span>₹{cgstTotal.toFixed(2)}</span>
                        </div>
                      </>
                    )}

                    {formData.taxType === 'IGST' && (
                      <div className="price-row">
                        <span>IGST ({formData.igstRate}%):</span>
                        <span>₹{igstTotal.toFixed(2)}</span>
                      </div>
                    )}

                    <div className="price-row total-row">
                      <span>Total Amount:</span>
                      <span>₹{calculateGrandTotal().toFixed(2)}</span>
                    </div>
                  </div>
                </div>

                {/* Terms & Conditions Section */}
                <div className="edit-section">
                  <div className="section-header">
                    <h3 className="section-title">Terms & Conditions</h3>
                    <button className="add-term-btn" onClick={addTerm}>
                      <Plus size={16} />
                      Add Term
                    </button>
                  </div>

                  <div className="terms-container">
                    {formData.termsandconditions.map((term, index) => (
                      <div key={index} className="term-item">
                        <input
                          type="text"
                          value={term}
                          onChange={(e) => handleTermChange(index, e.target.value)}
                          placeholder={`Term ${index + 1}`}
                        />
                        <button
                          className="remove-term-btn"
                          onClick={() => removeTerm(index)}
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="edit-invoice-footer">
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="btn-spinner"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditInvoicePopup;